import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { Button } from "@circleco/compass/components/Button";

import { t } from "@/i18n-js/instance";

import { useUpgradePlanContext } from "./../UpgradePlanContext";
import { UpgradePlanModalBody } from "./../UpgradePlanModalBody";
import { UpgradePlanModalFooter } from "./../UpgradePlanModalFooter";
import { UpgradePlanModalHeader } from "./../UpgradePlanModalHeader";
import { getDisplayName, getDisplaySubheading } from "./helpers";
import { PlanFeaturesList } from "./PlanFeaturesList";
import { PlanLineItems } from "./PlanLineItems";

const i18nRoot = "pricing_plans.upgrade_modal";

interface DowngradeIntervalDetailsProps {
  hideModal: () => void;
  onSubmitHandler: () => void;
  subheading?: string;
}

export const DowngradeIntervalDetails = ({
  subheading = "",
  hideModal,
  onSubmitHandler,
}: DowngradeIntervalDetailsProps) => {
  const { plan, emailHubPlan, isEmailHubOnlyUpgrade } = useUpgradePlanContext();

  const displayName = getDisplayName(plan, emailHubPlan);

  const intervalDowngradeFeatures = [
    t(`${i18nRoot}.interval_switch.downgrade.you_will_lose`, {
      savings: plan?.annual_discount_amount_with_currency,
    }),
    !plan?.eligible_for_schedule_downgrade &&
      t([i18nRoot, "interval_switch.downgrade.charges_effective"]),
  ].filter((item): item is string => Boolean(item));

  const displaySubheading = getDisplaySubheading(
    subheading,
    isEmailHubOnlyUpgrade,
  );

  return (
    <>
      <UpgradePlanModalHeader
        title={t([i18nRoot, "interval_downgrade_title"], {
          plan: displayName,
        })}
      >
        <Modal.Title>
          {t([i18nRoot, "interval_downgrade_title"], {
            plan: displayName,
          })}
        </Modal.Title>

        <Modal.HeaderActions>
          <Modal.CloseButton onClick={hideModal} />
        </Modal.HeaderActions>
      </UpgradePlanModalHeader>
      <UpgradePlanModalBody>
        {displaySubheading && (
          <div className="mb-6">
            <Typography.BodyMd color="text-default">
              {displaySubheading}
            </Typography.BodyMd>
          </div>
        )}

        <div className="mb-6">
          <Typography.LabelMd weight="semibold" size="text-md">
            {t([i18nRoot, "interval_switch.downgrade.cta"])}
            <span className="text-red-500">
              {t(`${i18nRoot}.interval_switch.downgrade.savings`, {
                savings: plan?.annual_discount_amount_with_currency,
              })}
            </span>
          </Typography.LabelMd>

          <Typography.BodySm weight="normal" size="text-sm">
            {t([i18nRoot, "interval_switch.downgrade.cta_subheading"])}
          </Typography.BodySm>
        </div>

        <PlanLineItems />
        <div className="mt-6">
          <div className="mt-3">
            <PlanFeaturesList
              features={intervalDowngradeFeatures}
              iconType="16-close"
              iconColor="text-red-500"
            />
          </div>
        </div>
      </UpgradePlanModalBody>
      <UpgradePlanModalFooter
        className="mt-5"
        primaryAction={{
          label: t([i18nRoot, "actions.interval_downgrade"], {
            plan: displayName,
          }),
          onClick: onSubmitHandler,
          shouldCloseModal: false,
        }}
      >
        <div className="flex justify-end">
          <Button variant="outline" size="lg" onClick={onSubmitHandler}>
            {t([i18nRoot, "actions.interval_downgrade"], {
              plan: displayName,
            })}
          </Button>
        </div>
      </UpgradePlanModalFooter>
    </>
  );
};
